import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import "../styles/LegalPages.css";

export default function TermsOfUsePage() {
  return (
    <>
      <Header />
      <main className="legal-page">
        <div className="legal-page__inner">
          <div className="legal-card">
            <header>
              <div className="legal-page__pill">
                <span className="legal-page__pill-dot" />
                Gail Thompson Foundation
              </div>
              <h1 className="legal-page__title">Terms of Use</h1>
              <p className="legal-page__meta">
                Last updated:{" "}
                <time dateTime="2025-11-23">November 23, 2025</time>
              </p>
            </header>

            <div className="legal-intro">
              <p>
                Gail Thompson Foundation (
                <strong>&quot;Gail Thompson Foundation&quot;</strong>,{" "}
                <strong>&quot;GTF&quot;</strong>, <strong>&quot;we&quot;</strong>,{" "}
                <strong>&quot;us&quot;</strong> or <strong>&quot;our&quot;</strong>
                ) operates this website and related online donation pages
                (collectively, the <strong>&quot;Site&quot;</strong>). Our
                mission is to honor Gail Thompson by raising funds to support
                Alzheimer&apos;s disease research and dementia care programs in
                Maine.
              </p>

              <p>
                These Terms of Use (the <strong>&quot;Terms&quot;</strong>)
                constitute a binding agreement between you and Gail Thompson
                Foundation. By accessing or using the Site, you acknowledge that
                you have read, understood, and agree to be bound by these Terms
                and by our{" "}
                <a
                  href="/privacy"
                  aria-label="Read our Security and Privacy Policy"
                >
                  Security &amp; Privacy Policy
                </a>
                . If you do not agree, you must not use the Site.
              </p>
            </div>

            <section className="legal-section">
              <h2 className="legal-section__heading">
                1. Not Medical, Legal, or Financial Advice
              </h2>
              <p>
                Content on the Site is provided for general informational and
                fundraising purposes only. It is not intended to be, and should
                not be relied upon as, medical, mental health, legal, tax, or
                financial advice.
              </p>
              <p>
                Always seek the advice of your physician or other qualified
                health provider with any questions you may have regarding a
                medical condition. Never disregard professional medical advice or
                delay seeking it because of something you have read on the Site.
                For legal, tax, or financial questions (including the
                deductibility of your donations), consult your own professional
                advisors.
              </p>
            </section>

            <section className="legal-section">
              <h2 className="legal-section__heading">
                2. Eligibility and Acceptable Use
              </h2>
              <p>
                The Site is intended for individuals who are at least 13 years
                old. If you are under 13, you may not use the Site or provide
                any personal information to us.
              </p>
              <p>When you use the Site, you agree that you will not:</p>
              <ul>
                <li>
                  Use the Site for any unlawful purpose or in violation of any
                  law or regulation.
                </li>
                <li>
                  Interfere with or disrupt the security, integrity, or
                  performance of the Site or our servers.
                </li>
                <li>
                  Attempt to gain unauthorized access to any portion of the Site,
                  other accounts, or computer systems.
                </li>
                <li>
                  Use the Site to send spam, solicitations, or other unsolicited
                  communications.
                </li>
                <li>
                  Upload, post, or transmit any content that is defamatory,
                  obscene, hateful, infringing, or otherwise objectionable.
                </li>
              </ul>
            </section>

            <section className="legal-section">
              <h2 className="legal-section__heading">
                3. Donations and Receipts
              </h2>
              <p>
                The Site allows you to make charitable donations in support of
                Gail Thompson Foundation&apos;s mission. Online credit or debit
                card transactions are processed by third-party payment
                processors (Stripe) on our
                behalf.
              </p>
              <ul>
                <li>
                  <strong>Payment information:</strong> We do not receive or
                  store your full payment card number or security code. Such
                  information is submitted directly to our payment processor and
                  handled under that provider&apos;s own terms and privacy
                  policy.
                </li>
                <li>
                  <strong>Accuracy of information:</strong> You are responsible
                  for providing accurate contact and payment information so that
                  we can process your donation and issue a receipt.
                </li>
                <li>
                  <strong>Tax deductibility:</strong> Donations may be
                  tax-deductible to the extent allowed by law. We will provide
                  an acknowledgment of your contribution, but you should consult
                  your tax advisor regarding your specific situation.
                </li>
                <li>
                  <strong>Refunds:</strong> As a general rule, charitable
                  contributions are not refundable. In limited circumstances,
                  and in our sole discretion, we may consider refund requests if
                  you contact us promptly after a transaction appears.
                </li>
              </ul>
            </section>

            <section className="legal-section">
              <h2 className="legal-section__heading">
                4. Accounts and Communications
              </h2>
              <p>
                The Site may allow or require you to provide contact information
                (for example, to receive email updates or donation receipts). If
                we later offer user accounts, you will be responsible for
                maintaining the confidentiality of your login credentials and
                for all activities that occur under your account.
              </p>
              <p>
                By providing your email address or phone number, you consent to
                receiving communications related to your donations, our
                programs, and other updates consistent with our{" "}
                <a href="/privacy">Security &amp; Privacy Policy</a>. You may
                opt out of non-essential communications at any time by following
                the unsubscribe link in our emails or by contacting us.
              </p>
            </section>

            <section className="legal-section">
              <h2 className="legal-section__heading">5. Intellectual Property</h2>
              <p>
                All content on the Site, including text, graphics, logos,
                photos, icons, and software, is owned by or licensed to Gail
                Thompson Foundation and is protected by U.S. and international
                copyright, trademark, and other intellectual property laws.
              </p>
              <p>
                You may view, download, or print reasonable portions of the Site
                content for your personal, non-commercial use in support of our
                mission, provided that you do not remove any copyright,
                trademark, or other proprietary notices. Any other use,
                including reproduction, modification, distribution, or
                republication, is prohibited without our prior written consent.
              </p>
            </section>

            <section className="legal-section">
              <h2 className="legal-section__heading">6. User Submissions</h2>
              <p>
                If you choose to submit content to us (for example, messages,
                testimonials, stories, photos, or other materials), you
                represent and warrant that you own or have the necessary rights
                to that content and that it does not infringe any third-party
                rights.
              </p>
              <p>
                By submitting content, you grant Gail Thompson Foundation a
                non-exclusive, royalty-free, worldwide license to use,
                reproduce, modify, publish, and display such content in any
                media now known or later developed, solely for the purpose of
                advancing our mission (for example, highlighting donor stories
                or showcasing impact), unless we agree otherwise with you in
                writing.
              </p>
            </section>

            <section className="legal-section">
              <h2 className="legal-section__heading">
                7. Third-Party Websites and Services
              </h2>
              <p>
                The Site may contain links to third-party websites or services
                (such as payment processors, event platforms, or partner
                organizations). These links are provided for convenience only.
              </p>
              <p>
                We do not control and are not responsible for the content,
                privacy practices, or security of any third-party sites. Your
                use of those sites is subject to their own terms and policies,
                not ours. Inclusion of a link does not imply our endorsement of
                the linked site or any products, services, or information
                provided by that site.
              </p>
            </section>

            <section className="legal-section">
              <h2 className="legal-section__heading">8. Disclaimers</h2>
              <p>
                TO THE FULLEST EXTENT PERMITTED BY LAW, THE SITE AND ALL
                CONTENT, PRODUCTS, AND SERVICES PROVIDED THROUGH THE SITE ARE
                OFFERED ON AN <strong>&quot;AS IS&quot;</strong> AND{" "}
                <strong>&quot;AS AVAILABLE&quot;</strong> BASIS WITHOUT
                WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED, OR STATUTORY.
              </p>
              <p>
                WITHOUT LIMITING THE FOREGOING, WE DISCLAIM ALL IMPLIED
                WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE,
                TITLE, AND NON-INFRINGEMENT, AS WELL AS ANY WARRANTIES ARISING
                FROM COURSE OF DEALING OR USAGE OF TRADE. WE DO NOT WARRANT THAT
                THE SITE WILL BE UNINTERRUPTED, SECURE, OR ERROR-FREE, OR THAT
                ANY DEFECTS WILL BE CORRECTED.
              </p>
            </section>

            <section className="legal-section">
              <h2 className="legal-section__heading">
                9. Limitation of Liability
              </h2>
              <p>
                TO THE FULLEST EXTENT PERMITTED BY LAW, IN NO EVENT WILL GAIL
                THOMPSON FOUNDATION OR ITS DIRECTORS, OFFICERS, VOLUNTEERS,
                EMPLOYEES, OR AGENTS BE LIABLE FOR ANY INDIRECT, INCIDENTAL,
                CONSEQUENTIAL, SPECIAL, EXEMPLARY, OR PUNITIVE DAMAGES ARISING
                OUT OF OR RELATED TO YOUR USE OF OR INABILITY TO USE THE SITE,
                EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
              </p>
              <p>
                TO THE FULLEST EXTENT PERMITTED BY LAW, OUR TOTAL AGGREGATE
                LIABILITY FOR ANY CLAIM ARISING OUT OF OR RELATING TO THE SITE
                OR THESE TERMS WILL NOT EXCEED ONE HUNDRED U.S. DOLLARS (US
                $100) OR, IF GREATER, THE AMOUNT YOU HAVE DONATED TO GAIL
                THOMPSON FOUNDATION THROUGH THE SITE IN THE TWELVE (12) MONTHS
                PRIOR TO THE EVENT GIVING RISE TO THE CLAIM.
              </p>
              <p>
                SOME JURISDICTIONS DO NOT ALLOW LIMITATIONS ON CERTAIN DAMAGES
                OR EXCLUSIONS OF IMPLIED WARRANTIES, SO SOME OF THE ABOVE
                LIMITATIONS MAY NOT APPLY TO YOU.
              </p>
            </section>

            <section className="legal-section">
              <h2 className="legal-section__heading">10. Indemnification</h2>
              <p>
                You agree to indemnify, defend, and hold harmless Gail Thompson
                Foundation and its directors, officers, volunteers, employees,
                and agents from and against any claims, liabilities, damages,
                losses, and expenses (including reasonable attorneys&apos; fees)
                arising out of or in any way connected with:
              </p>
              <ul>
                <li>Your access to or use of the Site;</li>
                <li>Your violation of these Terms; or</li>
                <li>Your violation of any third party&apos;s rights.</li>
              </ul>
            </section>

            <section className="legal-section">
              <h2 className="legal-section__heading">
                11. Changes to the Site and These Terms
              </h2>
              <p>
                We may update, modify, or discontinue any part of the Site at
                any time, with or without notice. We may also revise these
                Terms from time to time.
              </p>
              <p>
                When we make changes, we will update the &quot;Last
                updated&quot; date at the top of this page. The revised Terms
                will be effective when posted on the Site. Your continued use of
                the Site after the posting of revised Terms means that you
                accept and agree to the changes.
              </p>
            </section>

            <section className="legal-section">
              <h2 className="legal-section__heading">
                12. Governing Law and Jurisdiction
              </h2>
              <p>
                These Terms and your use of the Site are governed by the laws
                of the State of <strong>[STATE OF INCORPORATION]</strong>,
                U.S.A., without regard to its conflict of law principles.
              </p>
              <p>
                You agree that any dispute arising out of or relating to the
                Site or these Terms will be brought exclusively in the state or
                federal courts located in{" "}
                <strong>[CITY/COUNTY], [STATE OF INCORPORATION]</strong>, and
                you consent to the personal jurisdiction of such courts. If you
                are located outside the United States, you understand that by
                using the Site you are voluntarily submitting to the
                jurisdiction of U.S. courts.
              </p>
            </section>

            <section className="legal-section">
              <h2 className="legal-section__heading">13. Contact Us</h2>
              <p>
                If you have any questions about these Terms, please contact us
                using the information on our{" "}
                <a href="/contact" aria-label="Go to our contact page">
                  Contact
                </a>{" "}
                page or by email at{" "}
                <strong>
                  [insert contact email, e.g. info@gailthompsonfoundation.org]
                </strong>
                .
              </p>
            </section>

            <div className="legal-footer-note">
              This Terms of Use page is provided for general informational
              purposes only and does not constitute legal advice. Please consult
              with a qualified attorney before relying on or publishing this
              content.
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
